import { useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

import ButtonLink from '@v2/shared/components/ButtonLink';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

type Props = {
  /** The sale has not started yet, rather than having finished. */
  notYetActive: boolean;
  /** When the sale ended, already formatted by the server. */
  expiryDate: string | null;
};

/**
 * The sale is over, and this is the page saying so.
 *
 * A promotion has to disappear once it is done — an expired price a customer can still
 * see is a price they will expect to pay — so the page behind this holds the sale's
 * artwork and nothing else, and dismissing this takes the customer to the home page.
 * That is the legacy page's behaviour, down to the mechanism: `megasale/expired.html`
 * opens a Fancybox immediately with `data-onclose="goto-url" data-url="{shop.baseDir}"`.
 *
 * One panel, and one way on from it. Legacy paired the notice with a newsletter signup;
 * this offers the sales that are running instead, which is where somebody who has just
 * missed one actually wants to go. The cross does the same thing, so the dialog cannot be
 * dismissed onto a dead page whichever way it is closed.
 *
 * It shades what is behind it — `bg-slate-900/80`, the same overlay `shared/components/
 * Modal` draws — but it is still not full-screen on a phone. Those are separate
 * decisions: the shade says the page underneath is not what to read, the width keeps the
 * sale's artwork legible around it. `Modal` is `w-full h-full` below `md`, which is right
 * for the flows it serves and would hide the banner here; this is `max-w-md` at every
 * width, so the background stays visible on a phone as it does on a desktop.
 */
export default function SaleEndedDialog({ notYetActive, expiryDate }: Props) {
  const { brand } = useSite();
  const { buildUrl } = useUrlGenerator();
  const closeButtonRef = useRef(null);

  const homeUrl = buildUrl('/');
  const goHome = () => {
    window.location.href = homeUrl;
  };

  return (
    /*
      v2-root and data-brand for the same reason `Modal` carries them: HeadlessUI portals
      the dialog onto document.body, and every v2 rule is compiled scoped under .v2-root,
      so a portal that lands outside the mount wrapper opens completely unstyled.
    */
    <Dialog open className="v2-root" data-brand={brand} onClose={goHome} initialFocus={closeButtonRef}>
      <div className="fixed inset-0 z-[260] bg-slate-900/80" aria-hidden="true" />

      {/*
        Scrolled by the outer box and centred by the inner one, which is the arrangement
        that survives a panel taller than the screen: centring inside the scroll container
        itself puts the overflow above `scrollTop: 0`, and the top of the dialog becomes
        unreachable on a short phone.
      */}
      <div className="fixed inset-0 z-[270] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="relative w-full max-w-md bg-white rounded-md shadow-2xl overflow-hidden">
            <button
              type="button"
              onClick={goHome}
              ref={closeButtonRef}
              aria-label="Close"
              className="absolute top-1 right-1 z-10 w-8 h-8 p-0 border-0 bg-transparent text-white/80 hover:text-white cursor-pointer"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <div className="bg-slate-600 text-white text-center px-6 py-8">
              <Dialog.Title className="m-0 text-3xl font-bold">
                {notYetActive ? 'Not Valid Yet!' : 'You Missed it!'}
              </Dialog.Title>
              <p className="m-0 mt-2 font-bold">
                {notYetActive
                  ? 'This Promotion is Not Valid Yet'
                  : `This Promotion Ended${expiryDate ? ` on ${expiryDate}` : ''}`}
              </p>
            </div>

            {/*
              A real anchor rather than a button that navigates, because the destination is
              a url: a middle click opens it in a tab and the status bar says where it goes.
            */}
            <div className="px-6 py-6 text-center">
              <ButtonLink href={homeUrl}>Browse Our Latest Sales</ButtonLink>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
